import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";

// Dashboard Summary Interfaces
export interface InventorySummaryRequest {
  business_id: string;
}

export interface IngredientsStatus {
  total: number;
  low_stock: number;
  expired: number;
  expiring_soon: number;
}

export interface ProcessedItemsStatus {
  total: number;
  available: number;
  low_stock: number;
}

export interface SellableItemsStatus {
  total: number;
  available: number;
  out_of_stock: number;
}

export interface BatchesStatus {
  total: number;
  expired: number;
  expiring_soon: number;
}

export interface InventorySummaryResponse {
  success: boolean;
  data: {
    ingredients: IngredientsStatus;
    processed_items: ProcessedItemsStatus;
    sellable_items: SellableItemsStatus;
    batches: BatchesStatus;
  };
  message: string;
}

// Detailed Data Interfaces
export interface IngredientStatusItem {
  _id: string;
  name: string;
  brand_name?: string;
  quantity: number;
  unit: string;
  low_stock_threshold?: number;
  status: 'normal' | 'low_stock' | 'out_of_stock';
  expired_batches: number;
  expiring_soon_batches: number;
  last_updated?: string;
}

export interface ProcessedItemStatusItem {
  _id: string;
  name: string;
  quantity: number;
  unit: string;
  low_stock_threshold?: number;
  status: 'normal' | 'low_stock' | 'out_of_stock';
  last_produced?: string;
  shelf_life_days?: number;
  last_updated?: string;
}

export interface SellableItemStatusItem {
  _id: string;
  name: string;
  price: number;
  available: boolean;
  status: 'available' | 'out_of_stock';
  dependent_on_ingredients: Array<{
    ingredient_id: string;
    ingredient_name: string;
    status: 'normal' | 'low_stock' | 'out_of_stock'
  }>;
  last_updated?: string;
}

export interface BatchExpiryItem {
  _id: string;
  batch_id: string;
  ingredient_id: string;
  ingredient_name: string;
  quantity: number;
  unit: string;
  expiry_date: string;
  days_until_expiry: number;
  status: 'expired' | 'expiring_soon' | 'normal';
  storage_location?: string;
  last_updated?: string;
}

// List Request Parameters
export interface ListRequestParams {
  business_id: string;
  pageIndex?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Array<{
    field: string;
    operator: 'equals' | 'contains' | 'greater_than' | 'less_than';
    value: string | number | boolean;
  }>;
  search?: string;
}

// List Response Interfaces
export interface IngredientStatusResponse {
  success: boolean;
  data: {
    items: IngredientStatusItem[];
    total: number;
  };
  message: string;
}

export interface ProcessedItemStatusResponse {
  success: boolean;
  data: {
    items: ProcessedItemStatusItem[];
    total: number;
  };
  message: string;
}

export interface SellableItemStatusResponse {
  success: boolean;
  data: {
    items: SellableItemStatusItem[];
    total: number;
  };
  message: string;
}

export interface BatchExpiryResponse {
  success: boolean;
  data: {
    items: BatchExpiryItem[];
    total: number;
  };
  message: string;
}

// Trend Data Interfaces
export interface InventoryTrendDataPoint {
  date: string;
  value: number;
}

export interface InventoryTrendRequest {
  business_id: string;
  category: 'ingredients' | 'processed_items' | 'sellable_items' | 'batches';
  metric: 'total' | 'low_stock' | 'out_of_stock' | 'expired' | 'expiring_soon';
  timeframe: 'week' | 'month' | 'quarter' | 'year';
}

export interface InventoryTrendResponse {
  success: boolean;
  data: {
    trend: InventoryTrendDataPoint[];
    category: string;
    metric: string;
    timeframe: string;
  };
  message: string;
}

// Ingredient Dependencies Interfaces
export interface IngredientDependencyNode {
  id: string;
  type: 'ingredient' | 'processed_item' | 'sellable_item';
  name: string;
  status: 'normal' | 'low_stock' | 'out_of_stock' | 'expired' | 'expiring_soon';
}

export interface IngredientDependencyLink {
  source: string;
  target: string;
  quantity?: number;
  unit?: string;
}

export interface IngredientDependencyRequest {
  business_id: string;
  root_id?: string;  // Optional: focus on a specific ingredient, processed item, or sellable item
  depth?: number;    // How many levels to include (1-3)
}

export interface IngredientDependencyResponse {
  success: boolean;
  data: {
    nodes: IngredientDependencyNode[];
    links: IngredientDependencyLink[];
  };
  message: string;
}

// Mock data for development while backend is being fixed
const MOCK_INVENTORY_SUMMARY = {
  success: true,
  data: {
    ingredients: {
      total: 100,
      low_stock: 10,
      expired: 5,
      expiring_soon: 8
    },
    processed_items: {
      total: 50,
      available: 45,
      low_stock: 5
    },
    sellable_items: {
      total: 75,
      available: 70,
      out_of_stock: 5
    },
    batches: {
      total: 120,
      expired: 10,
      expiring_soon: 15
    }
  },
  message: "Mock inventory summary for development"
};

// Mock data for detailed views
const MOCK_INGREDIENT_STATUS: IngredientStatusResponse = {
  success: true,
  data: {
    items: [
      {
        _id: "ing-001",
        name: "Flour",
        brand_name: "Premium Baking",
        quantity: 25.5,
        unit: "kg",
        low_stock_threshold: 10,
        status: "normal",
        expired_batches: 0,
        expiring_soon_batches: 1,
        last_updated: new Date().toISOString()
      },
      {
        _id: "ing-002",
        name: "Sugar",
        brand_name: "Sweet Crystals",
        quantity: 8.2,
        unit: "kg",
        low_stock_threshold: 10,
        status: "low_stock",
        expired_batches: 0,
        expiring_soon_batches: 0,
        last_updated: new Date().toISOString()
      },
      {
        _id: "ing-003",
        name: "Salt",
        brand_name: "Sea Salt Premium",
        quantity: 0,
        unit: "kg",
        low_stock_threshold: 1,
        status: "out_of_stock",
        expired_batches: 0,
        expiring_soon_batches: 0,
        last_updated: new Date().toISOString()
      },
      {
        _id: "ing-004",
        name: "Eggs",
        brand_name: "Farm Fresh",
        quantity: 240,
        unit: "pcs",
        low_stock_threshold: 50,
        status: "normal",
        expired_batches: 1,
        expiring_soon_batches: 2,
        last_updated: new Date().toISOString()
      },
      {
        _id: "ing-005",
        name: "Milk",
        brand_name: "Dairy Delight",
        quantity: 35.5,
        unit: "liters",
        low_stock_threshold: 20,
        status: "normal",
        expired_batches: 0,
        expiring_soon_batches: 3,
        last_updated: new Date().toISOString()
      }
    ],
    total: 5
  },
  message: "Mock ingredient status data"
};

const MOCK_PROCESSED_ITEM_STATUS: ProcessedItemStatusResponse = {
  success: true,
  data: {
    items: [
      {
        _id: "proc-001",
        name: "Bread Dough",
        quantity: 15.3,
        unit: "kg",
        low_stock_threshold: 5,
        status: "normal",
        last_produced: new Date().toISOString(),
        shelf_life_days: 2,
        last_updated: new Date().toISOString()
      },
      {
        _id: "proc-002",
        name: "Pizza Base",
        quantity: 3.2,
        unit: "kg",
        low_stock_threshold: 5,
        status: "low_stock",
        last_produced: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        shelf_life_days: 3,
        last_updated: new Date().toISOString()
      },
      {
        _id: "proc-003",
        name: "Tomato Sauce",
        quantity: 0,
        unit: "liters",
        low_stock_threshold: 2,
        status: "out_of_stock",
        last_produced: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        shelf_life_days: 7,
        last_updated: new Date().toISOString()
      }
    ],
    total: 3
  },
  message: "Mock processed item status data"
};

const MOCK_SELLABLE_ITEM_STATUS: SellableItemStatusResponse = {
  success: true,
  data: {
    items: [
      {
        _id: "sell-001",
        name: "Cheese Pizza",
        price: 12.99,
        available: true,
        status: "available",
        dependent_on_ingredients: [
          {
            ingredient_id: "ing-001",
            ingredient_name: "Flour",
            status: "normal"
          },
          {
            ingredient_id: "ing-002",
            ingredient_name: "Sugar",
            status: "low_stock"
          }
        ],
        last_updated: new Date().toISOString()
      },
      {
        _id: "sell-002",
        name: "Garlic Bread",
        price: 4.99,
        available: true,
        status: "available",
        dependent_on_ingredients: [
          {
            ingredient_id: "ing-001",
            ingredient_name: "Flour",
            status: "normal"
          }
        ],
        last_updated: new Date().toISOString()
      },
      {
        _id: "sell-003",
        name: "Tomato Pasta",
        price: 9.99,
        available: false,
        status: "out_of_stock",
        dependent_on_ingredients: [
          {
            ingredient_id: "ing-003",
            ingredient_name: "Salt",
            status: "out_of_stock"
          }
        ],
        last_updated: new Date().toISOString()
      }
    ],
    total: 3
  },
  message: "Mock sellable item status data"
};

const MOCK_BATCH_EXPIRY: BatchExpiryResponse = {
  success: true,
  data: {
    items: [
      {
        _id: "batch-001",
        batch_id: "BTC-1001",
        ingredient_id: "ing-004",
        ingredient_name: "Eggs",
        quantity: 30,
        unit: "pcs",
        expiry_date: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        days_until_expiry: -1,
        status: "expired",
        storage_location: "Main Refrigerator",
        last_updated: new Date().toISOString()
      },
      {
        _id: "batch-002",
        batch_id: "BTC-1002",
        ingredient_id: "ing-005",
        ingredient_name: "Milk",
        quantity: 5.5,
        unit: "liters",
        expiry_date: new Date(Date.now() + 86400000).toISOString(), // Tomorrow
        days_until_expiry: 1,
        status: "expiring_soon",
        storage_location: "Cold Storage 2",
        last_updated: new Date().toISOString()
      },
      {
        _id: "batch-003",
        batch_id: "BTC-1003",
        ingredient_id: "ing-001",
        ingredient_name: "Flour",
        quantity: 10,
        unit: "kg",
        expiry_date: new Date(Date.now() + 172800000).toISOString(), // 2 days from now
        days_until_expiry: 2,
        status: "expiring_soon",
        storage_location: "Dry Storage A",
        last_updated: new Date().toISOString()
      },
      {
        _id: "batch-004",
        batch_id: "BTC-1004",
        ingredient_id: "ing-004",
        ingredient_name: "Eggs",
        quantity: 60,
        unit: "pcs",
        expiry_date: new Date(Date.now() + 259200000).toISOString(), // 3 days from now
        days_until_expiry: 3,
        status: "expiring_soon",
        storage_location: "Main Refrigerator",
        last_updated: new Date().toISOString()
      }
    ],
    total: 4
  },
  message: "Mock batch expiry data"
};

// Mock trend data
const MOCK_TREND_DATA: Record<string, Record<string, InventoryTrendDataPoint[]>> = {
  ingredients: {
    total: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 95 + Math.floor(Math.random() * 10)
    })),
    low_stock: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 8 + Math.floor(Math.random() * 6) - (i > 20 ? 2 : 0)
    })),
    expired: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 3 + Math.floor(Math.random() * 4)
    }))
  },
  processed_items: {
    total: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 45 + Math.floor(Math.random() * 8)
    })),
    low_stock: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 4 + Math.floor(Math.random() * 3)
    }))
  },
  sellable_items: {
    total: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 70 + Math.floor(Math.random() * 10)
    })),
    out_of_stock: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 4 + Math.floor(Math.random() * 3) + (i > 25 ? 2 : 0)
    }))
  },
  batches: {
    total: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 115 + Math.floor(Math.random() * 15)
    })),
    expired: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 8 + Math.floor(Math.random() * 4)
    })),
    expiring_soon: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: 12 + Math.floor(Math.random() * 6)
    }))
  }
};

// Mock dependency data
const MOCK_DEPENDENCY_DATA: IngredientDependencyResponse = {
  success: true,
  data: {
    nodes: [
      { id: "ing-001", type: "ingredient", name: "Flour", status: "normal" },
      { id: "ing-002", type: "ingredient", name: "Sugar", status: "low_stock" },
      { id: "ing-003", type: "ingredient", name: "Eggs", status: "normal" },
      { id: "ing-004", type: "ingredient", name: "Butter", status: "normal" },
      { id: "ing-005", type: "ingredient", name: "Milk", status: "normal" },
      { id: "ing-006", type: "ingredient", name: "Yeast", status: "out_of_stock" },
      { id: "proc-001", type: "processed_item", name: "Cake Base", status: "normal" },
      { id: "proc-002", type: "processed_item", name: "Bread Dough", status: "low_stock" },
      { id: "proc-003", type: "processed_item", name: "Cookie Mix", status: "normal" },
      { id: "sell-001", type: "sellable_item", name: "Chocolate Cake", status: "normal" },
      { id: "sell-002", type: "sellable_item", name: "Bread Loaf", status: "out_of_stock" },
      { id: "sell-003", type: "sellable_item", name: "Cookies", status: "normal" }
    ],
    links: [
      { source: "proc-001", target: "ing-001", quantity: 2, unit: "kg" },
      { source: "proc-001", target: "ing-002", quantity: 1, unit: "kg" },
      { source: "proc-001", target: "ing-003", quantity: 6, unit: "pcs" },
      { source: "proc-001", target: "ing-004", quantity: 0.5, unit: "kg" },
      { source: "proc-002", target: "ing-001", quantity: 1, unit: "kg" },
      { source: "proc-002", target: "ing-006", quantity: 0.05, unit: "kg" },
      { source: "proc-002", target: "ing-005", quantity: 0.2, unit: "liter" },
      { source: "proc-003", target: "ing-001", quantity: 1, unit: "kg" },
      { source: "proc-003", target: "ing-002", quantity: 0.8, unit: "kg" },
      { source: "proc-003", target: "ing-004", quantity: 0.3, unit: "kg" },
      { source: "sell-001", target: "proc-001", quantity: 1, unit: "unit" },
      { source: "sell-002", target: "proc-002", quantity: 1, unit: "unit" },
      { source: "sell-003", target: "proc-003", quantity: 1, unit: "unit" }
    ]
  },
  message: "Mock ingredient dependency data"
};

class InventoryDashboardService {
  // Base URL for dashboard endpoints
  private readonly baseUrl = "/business-admin/inventory-dashboard";
  
  // Flag to control mock data usage
  private useMockData = false;

  /**
   * Toggle mock data mode
   * @param use - Whether to use mock data
   */
  setUseMockData(use: boolean): void {
    this.useMockData = use;
    console.log(`Mock data mode ${use ? 'enabled' : 'disabled'} for inventory dashboard`);
  }

  /**
   * Get inventory summary data
   * @param businessId - The business ID
   * @returns Summary of inventory items and their statuses
   */
  async getInventorySummary(businessId: string): Promise<InventorySummaryResponse> {
    // If mock data is enabled, return it immediately
    if (this.useMockData) {
      console.log("Using mock data for inventory summary");
      return MOCK_INVENTORY_SUMMARY;
    }
    
    try {
      console.log(`Fetching inventory summary for business ID: ${businessId}`);
      
      const response = await axiosInstance.post(
        `${this.baseUrl}/summary`,
        { business_id: businessId }
      );
      
      console.log("Inventory summary response:", response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // If we get here, the API returned a failure response
      console.warn("API returned failure:", response.data);
      
      // Check if this is the specific Elasticsearch index error
      if (response.data?.error?.details?.includes("index_not_found_exception")) {
        console.error(
          "Elasticsearch index not found error. This indicates that the required index " +
          "has not been created in Elasticsearch or is incorrectly named in the backend code. " +
          "The backend team should check: \n" +
          "1. That all required Elasticsearch indices have been created \n" +
          "2. That index names are correctly referenced in API code \n" +
          "3. That any required data migrations have been run"
        );
        console.warn("Falling back to mock data for development");
        return MOCK_INVENTORY_SUMMARY;
      }
      
      throw new Error(response.data?.error?.message || "Failed to fetch inventory summary");
    } catch (error: any) {
      console.error("Error fetching inventory summary:", error);
      
      // If this is an Axios error with a response, log more details
      if (error.response) {
        console.error("Server responded with error status:", error.response.status);
        console.error("Error response data:", error.response.data);
        
        // Special handling for common status codes
        if (error.response.status === 404) {
          console.error("API endpoint not found. Check that the URL is correct.");
        } else if (error.response.status === 500) {
          console.error(
            "Internal server error. This could be related to Elasticsearch configuration, " +
            "missing indices, or other server-side issues."
          );
        }
      } else if (error.request) {
        console.error("No response received from server. Check network connectivity.");
      } else {
        console.error("Error setting up request:", error.message);
      }
      
      // In case of error, fallback to mock data in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn("Falling back to mock data in development mode");
        return MOCK_INVENTORY_SUMMARY;
      }
      
      // Return a default/empty response structure on error in production
      return {
        success: false,
        data: {
          ingredients: { total: 0, low_stock: 0, expired: 0, expiring_soon: 0 },
          processed_items: { total: 0, available: 0, low_stock: 0 },
          sellable_items: { total: 0, available: 0, out_of_stock: 0 },
          batches: { total: 0, expired: 0, expiring_soon: 0 }
        },
        message: "Error fetching inventory summary"
      };
    }
  }

  /**
   * Get detailed ingredient status data
   * @param params - Request parameters including pagination, sorting, and filtering
   * @returns List of ingredients with their status information
   */
  async getIngredientStatus(params: ListRequestParams): Promise<IngredientStatusResponse> {
    if (this.useMockData) {
      console.log("Using mock data for ingredient status");
      return MOCK_INGREDIENT_STATUS;
    }
    
    try {
      console.log(`Fetching ingredient status for business ID: ${params.business_id}`);
      
      const response = await axiosInstance.post(
        `${this.baseUrl}/ingredients/status`,
        params
      );
      
      console.log("Ingredient status response:", response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // If we get here, the API returned a failure response
      console.warn("API returned failure:", response.data);
      
      throw new Error(response.data?.error?.message || "Failed to fetch ingredient status");
    } catch (error: any) {
      console.error("Error fetching ingredient status:", error);
      
      // In case of error, fallback to mock data in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn("Falling back to mock data for ingredient status");
        return MOCK_INGREDIENT_STATUS;
      }
      
      // Return a default/empty response structure on error in production
      return {
        success: false,
        data: {
          items: [],
          total: 0
        },
        message: "Error fetching ingredient status"
      };
    }
  }

  /**
   * Get detailed processed item status data
   * @param params - Request parameters including pagination, sorting, and filtering
   * @returns List of processed items with their status information
   */
  async getProcessedItemStatus(params: ListRequestParams): Promise<ProcessedItemStatusResponse> {
    if (this.useMockData) {
      console.log("Using mock data for processed item status");
      return MOCK_PROCESSED_ITEM_STATUS;
    }
    
    try {
      console.log(`Fetching processed item status for business ID: ${params.business_id}`);
      
      const response = await axiosInstance.post(
        `${this.baseUrl}/processed-items/status`,
        params
      );
      
      console.log("Processed item status response:", response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // If we get here, the API returned a failure response
      console.warn("API returned failure:", response.data);
      
      throw new Error(response.data?.error?.message || "Failed to fetch processed item status");
    } catch (error: any) {
      console.error("Error fetching processed item status:", error);
      
      // In case of error, fallback to mock data in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn("Falling back to mock data for processed item status");
        return MOCK_PROCESSED_ITEM_STATUS;
      }
      
      // Return a default/empty response structure on error in production
      return {
        success: false,
        data: {
          items: [],
          total: 0
        },
        message: "Error fetching processed item status"
      };
    }
  }

  /**
   * Get detailed sellable item status data
   * @param params - Request parameters including pagination, sorting, and filtering
   * @returns List of sellable items with their status information
   */
  async getSellableItemStatus(params: ListRequestParams): Promise<SellableItemStatusResponse> {
    if (this.useMockData) {
      console.log("Using mock data for sellable item status");
      return MOCK_SELLABLE_ITEM_STATUS;
    }
    
    try {
      console.log(`Fetching sellable item status for business ID: ${params.business_id}`);
      
      const response = await axiosInstance.post(
        `${this.baseUrl}/sellable-items/status`,
        params
      );
      
      console.log("Sellable item status response:", response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // If we get here, the API returned a failure response
      console.warn("API returned failure:", response.data);
      
      throw new Error(response.data?.error?.message || "Failed to fetch sellable item status");
    } catch (error: any) {
      console.error("Error fetching sellable item status:", error);
      
      // In case of error, fallback to mock data in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn("Falling back to mock data for sellable item status");
        return MOCK_SELLABLE_ITEM_STATUS;
      }
      
      // Return a default/empty response structure on error in production
      return {
        success: false,
        data: {
          items: [],
          total: 0
        },
        message: "Error fetching sellable item status"
      };
    }
  }

  /**
   * Get detailed batch expiry data
   * @param params - Request parameters including pagination, sorting, and filtering
   * @returns List of batches with their expiry information
   */
  async getBatchExpiry(params: ListRequestParams): Promise<BatchExpiryResponse> {
    if (this.useMockData) {
      console.log("Using mock data for batch expiry");
      return MOCK_BATCH_EXPIRY;
    }
    
    try {
      console.log(`Fetching batch expiry data for business ID: ${params.business_id}`);
      
      const response = await axiosInstance.post(
        `${this.baseUrl}/expiry-alerts`,
        params
      );
      
      console.log("Batch expiry response:", response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // If we get here, the API returned a failure response
      console.warn("API returned failure:", response.data);
      
      throw new Error(response.data?.error?.message || "Failed to fetch batch expiry data");
    } catch (error: any) {
      console.error("Error fetching batch expiry data:", error);
      
      // In case of error, fallback to mock data in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn("Falling back to mock data for batch expiry");
        return MOCK_BATCH_EXPIRY;
      }
      
      // Return a default/empty response structure on error in production
      return {
        success: false,
        data: {
          items: [],
          total: 0
        },
        message: "Error fetching batch expiry data"
      };
    }
  }

  // Get inventory trend data
  async getInventoryTrend(request: InventoryTrendRequest): Promise<InventoryTrendResponse> {
    try {
      if (this.useMockData) {
        // Return mock data
        const category = request.category;
        const metric = request.metric;
        const trendData = MOCK_TREND_DATA[category]?.[metric] || [];
        
        return {
          success: true,
          data: {
            trend: trendData,
            category: request.category,
            metric: request.metric,
            timeframe: request.timeframe
          },
          message: "Mock trend data returned successfully"
        };
      }
      
      // Call the API endpoint
      const response: AxiosResponse<InventoryTrendResponse> = await axiosInstance.get(
        `${this.baseUrl}/trends`, 
        { params: request }
      );
      
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory trend data:", error);
      // Return mock data as fallback for development
      const category = request.category;
      const metric = request.metric;
      const trendData = MOCK_TREND_DATA[category]?.[metric] || [];
      
      return {
        success: false,
        data: {
          trend: trendData,
          category: request.category,
          metric: request.metric,
          timeframe: request.timeframe
        },
        message: "Error fetching inventory trend data"
      };
    }
  }
  
  // Get ingredient dependencies
  async getIngredientDependencies(request: IngredientDependencyRequest): Promise<IngredientDependencyResponse> {
    try {
      if (this.useMockData) {
        // Return mock data
        return MOCK_DEPENDENCY_DATA;
      }
      
      // Call the API endpoint
      const response: AxiosResponse<IngredientDependencyResponse> = await axiosInstance.get(
        `${this.baseUrl}/dependencies`, 
        { params: request }
      );
      
      return response.data;
    } catch (error) {
      console.error("Error fetching ingredient dependencies:", error);
      // Return mock data as fallback for development
      return {
        success: false,
        data: MOCK_DEPENDENCY_DATA.data,
        message: "Error fetching ingredient dependencies"
      };
    }
  }
}

// Export a singleton instance
export const inventoryDashboardService = new InventoryDashboardService(); 